import { useCallback, useMemo } from "react";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import ActionsByCategory from "../ActionsByCategory/ActionsByCategory";
import { READ_ONLY_ACCESS_KEY, readOnlyAccessRow } from "../readOnlyAccessRow";
import type { ActionItem } from "../../types";
import styles from "./ActionsGrid.module.css";

interface ActionsGridProps {
  actions: ActionItem[];
  values: Record<string, boolean>;
  onToggle: (actionKey: string) => void;

  onBulkChange?: (nextValues: Record<string, boolean>) => void;

  readOnlyAccess?: boolean;
  onReadOnlyAccessChange?: (nextValue: boolean) => void;

  disabled?: boolean;
}

function ActionsGrid({
  actions,
  values,
  onToggle,
  onBulkChange,
  readOnlyAccess = false,
  onReadOnlyAccessChange,
  disabled = false,
}: ActionsGridProps) {
  const handleToggleReadOnly = useCallback(() => {
    if (disabled) return;
    const next = !readOnlyAccess;
    // Turning ON also clears the draft actions so the change is visually
    // consistent (locked switches match a cleared draft) and marks the form
    // dirty (activates Save). Guarded on `hasAnyOn` to avoid dirtying the
    // form for a no-op transition on a role that already has zero actions.
    // The BE would silently discard actions submitted alongside the flag,
    // but clearing on the client keeps the payload aligned with the UI.
    if (next && onBulkChange) {
      const hasAnyOn = actions.some((a) => Boolean(values[a.key]));
      if (hasAnyOn) {
        const cleared = actions.reduce<Record<string, boolean>>((acc, a) => {
          acc[a.key] = false;
          return acc;
        }, {});
        onBulkChange(cleared);
      }
    }
    onReadOnlyAccessChange?.(next);
  }, [disabled, readOnlyAccess, onReadOnlyAccessChange, onBulkChange, actions, values]);

  // While Read Only is ON, every action switch is visually forced OFF and locked.
  // The visual override is defensive — even if the parent doesn't wire
  // `onBulkChange`, the switches stay consistent with the Read Only state.
  const actionsLocked = readOnlyAccess || disabled;

  // The lock leads the list: it maps to General (see ACTION_CATEGORY_BY_KEY)
  // and General is the first category. It stays operable while it locks
  // everything else, which is why the lock is per-row rather than column-wide.
  const rows = useMemo(
    () => [
      { ...readOnlyAccessRow(), disabled },
      ...(actions ?? []).map((action) => ({ ...action, disabled: actionsLocked })),
    ],
    [actions, disabled, actionsLocked],
  );

  const rowValues = useMemo(
    () => ({
      ...(readOnlyAccess ? null : values),
      [READ_ONLY_ACCESS_KEY]: readOnlyAccess,
    }),
    [values, readOnlyAccess],
  );

  if (!actions?.length) return null;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.label}>{AppStrings.ACTIONS}</span>
      </div>
      <ActionsByCategory
        actions={rows}
        values={rowValues}
        onActionToggle={({ eventCode }) => {
          // The lock is a role field, not a member of `actions[]`.
          if (eventCode === READ_ONLY_ACCESS_KEY) {
            handleToggleReadOnly();
            return;
          }
          if (actionsLocked) return;
          onToggle(eventCode);
        }}
      />
    </div>
  );
}

export default ActionsGrid;
